import { useMemo } from "react"
import { GetApp } from "../../App"
import { OverlayView, OverlayViewDefinitions } from "../OverlayView"
import { UserAdviceInstance, UserAdviceState } from "./Advice"

// The name of the "nothing open" overlay, mirroring the `none` member every other
// overlay hub declares (`AppOverlayContent.none`, `ShipViewOverlayContent.none`).
// The other names are not an enum here: an advice overlay is identified by the id of
// the advice that opens it, and which advices exist is data -- the open alerts, the
// running watches -- rather than a fixed list.
export const UserAdviceOverlayContent = {
  none: "none",
};

interface UserAdviceOverlayViewProps {
  advices: UserAdviceInstance[];
  state: UserAdviceState;
}

// The single host for every advice dialog, the way `AppOverlayContentView` and
// `ShipOverlayContentView` host theirs (#1171). Before this the dialog was rendered
// beside its own button, so the open overlay's position in the tree depended on which
// row happened to own the advice.
//
// Each definition is built by the advice itself -- `advice.overlay()` delegates to its
// dialog module's `*Overlay` factory -- so the title, severity styling and closability
// stay with the dialog that decides them, and this view only routes.
export function UserAdviceOverlayView({ advices, state }: UserAdviceOverlayViewProps) {
  const app = GetApp();

  const overlays = useMemo(() => {
    const definitions: OverlayViewDefinitions = {
      [UserAdviceOverlayContent.none]: { render: () => null },
    };
    for (const advice of advices) {
      const overlay = advice.overlay(app, state);
      if (overlay) {
        definitions[advice.id] = overlay;
      }
    }
    return definitions;
  }, [advices, app, state]);

  // An advice can vanish while its dialog is open -- the alert clears, the watch
  // stops, the day rolls over -- and `OverlayView` throws on a name it has no
  // definition for. Fall back to `none` so a disappearing advice simply closes its
  // dialog instead of taking the view down with it.
  const selected = state.selected && overlays[state.selected] ? state.selected : UserAdviceOverlayContent.none;

  return (
    <OverlayView
      className="UserAdviceOverlay"
      overlay={selected}
      overlays={overlays}
      resetOverlay={() => state.setSelected(undefined)}
    />
  );
}
